import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import { IconLoader, IconPlus, IconTrash } from "../components/Icons";
import { useMsg } from "../hooks/useMsg";
import NumberingSettings from "../components/NumberingSettings";
import Toggle from "../components/Toggle";

interface TenantProfile {
  id: string;
  name: string;
  is_vat_payer: boolean;
  stock_include_open_orders: boolean;
  eet_mode: string;
}

interface CustomPaymentMethod {
  id: string;
  code: string;
  name: string;
  icon: string;
  color: string | null;
  eet_enabled: boolean;
  is_active: boolean;
  sort_order: number;
}

const PM_ICONS = ["💳", "🎫", "🧾", "🏷️", "📄", "💶", "🤝", "🍽️", "🎁", "📱", "🔖", "🅿️"];

interface VatRate {
  id: string;
  code: string;
  name: string;
  rate: number;
  is_default: boolean;
  is_active: boolean;
}

export default function OperationSettings() {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<TenantProfile | null>(null);
  const [vatRates, setVatRates] = useState<VatRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const setMsg = useMsg();

  const [newRate, setNewRate] = useState({ code: "", name: "", rate: "" });
  const [payMethods, setPayMethods] = useState<CustomPaymentMethod[]>([]);
  const [editingPm, setEditingPm] = useState<CustomPaymentMethod | null>(null);
  const emptyPm = { code: "", name: "", icon: "💳", color: "#4f7cff", eet_enabled: true, sort_order: 0 };
  const [pmForm, setPmForm] = useState(emptyPm);

  function errorText(e: any): string {
    const detail = e?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((d: any) => d.msg || String(d)).join("; ");
    return t("common.error");
  }

  async function load() {
    setLoading(true);
    try {
      const [pRes, vRes, pmRes] = await Promise.all([
        api.get<TenantProfile>("/tenant/profile"),
        api.get<VatRate[]>("/catalog/vat-rates"),
        api.get<CustomPaymentMethod[]>("/tenant/payment-methods"),
      ]);
      setProfile(pRes.data);
      setVatRates(vRes.data);
      setPayMethods(pmRes.data);
    } catch (e: any) {
      setMsg({ type: "err", text: t("opSettings.loadError") });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function saveVatPayer(isVatPayer: boolean) {
    if (!profile) return;
    setSaving(true);
    setMsg(null);
    try {
      const { data } = await api.put<TenantProfile>("/tenant/profile", { is_vat_payer: isVatPayer });
      setProfile(data);
      setMsg({ type: "ok", text: t("companyProfile.saved") });
    } catch (e: any) {
      setMsg({ type: "err", text: errorText(e) || t("opSettings.saveError") });
    } finally {
      setSaving(false);
    }
  }

  async function saveStockIncludeOpen(checked: boolean) {
    if (!profile) return;
    setSaving(true);
    setMsg(null);
    try {
      const { data } = await api.put<TenantProfile>("/tenant/profile", { stock_include_open_orders: checked });
      setProfile(data);
      setMsg({ type: "ok", text: t("companyProfile.saved") });
    } catch (e: any) {
      setMsg({ type: "err", text: errorText(e) || t("opSettings.saveError") });
    } finally {
      setSaving(false);
    }
  }

  async function createVatRate(e: React.FormEvent) {
    e.preventDefault();
    if (!newRate.rate) return;
    try {
      await api.post("/catalog/vat-rates", {
        ...(newRate.code.trim() ? { code: newRate.code.trim() } : {}),
        ...(newRate.name.trim() ? { name: newRate.name.trim() } : {}),
        rate: parseFloat(newRate.rate),
      });
      setNewRate({ code: "", name: "", rate: "" });
      await load();
    } catch (e: any) {
      setMsg({ type: "err", text: errorText(e) || t("opSettings.createRateError") });
    }
  }

  async function updateVatRate(id: string, patch: Partial<VatRate>) {
    try {
      await api.put(`/catalog/vat-rates/${id}`, patch);
      await load();
    } catch (e: any) {
      setMsg({ type: "err", text: errorText(e) || t("opSettings.updateRateError") });
    }
  }

  async function savePayMethod(e: React.FormEvent) {
    e.preventDefault();
    if (!pmForm.name.trim() || (!editingPm && !pmForm.code.trim())) return;
    setSaving(true);
    setMsg(null);
    try {
      if (editingPm) {
        await api.put(`/tenant/payment-methods/${editingPm.id}`, {
          name: pmForm.name, icon: pmForm.icon, color: pmForm.color || null,
          eet_enabled: pmForm.eet_enabled, sort_order: pmForm.sort_order,
        });
      } else {
        await api.post("/tenant/payment-methods", {
          code: pmForm.code, name: pmForm.name, icon: pmForm.icon,
          color: pmForm.color || null, eet_enabled: pmForm.eet_enabled,
          sort_order: pmForm.sort_order,
        });
      }
      setEditingPm(null);
      setPmForm(emptyPm);
      const { data } = await api.get<CustomPaymentMethod[]>("/tenant/payment-methods");
      setPayMethods(data);
      setMsg({ type: "ok", text: t("companyProfile.saved") });
    } catch (e: any) {
      setMsg({ type: "err", text: errorText(e) || t("opSettings.saveError") });
    } finally {
      setSaving(false);
    }
  }

  async function togglePayMethod(m: CustomPaymentMethod, patch: Partial<CustomPaymentMethod>) {
    try {
      await api.put(`/tenant/payment-methods/${m.id}`, patch);
      setPayMethods((prev) => prev.map((x) => x.id === m.id ? { ...x, ...patch } : x));
    } catch (e: any) {
      setMsg({ type: "err", text: errorText(e) || t("opSettings.saveError") });
    }
  }

  async function deleteVatRate(id: string) {
    if (!confirm(t("opSettings.deleteRateConfirm"))) return;
    try {
      await api.delete(`/catalog/vat-rates/${id}`);
      await load();
    } catch (e: any) {
      setMsg({ type: "err", text: errorText(e) || t("opSettings.deleteRateError") });
    }
  }

  if (loading || !profile) {
    return <div className="card" style={{ textAlign: "center", padding: 48 }}><IconLoader size={32} className="muted" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1>{t("opSettings.title")}</h1>
        <p>{t("opSettings.subtitle")}</p>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>{t("opSettings.vatRates")}</h3>

        <div style={{ marginBottom: 20 }}>
          <Toggle
            checked={profile.is_vat_payer}
            onChange={(checked) => saveVatPayer(checked)}
            label={t("opSettings.vatPayer")}
            disabled={saving}
          />
        </div>

        {profile.is_vat_payer ? (
          <>
            <table style={{ marginBottom: 16 }}>
              <thead>
                <tr>
                  <th>{t("customers.colCode")}</th>
                  <th>{t("common.name")}</th>
                  <th>{t("opSettings.ratePct")}</th>
                  <th>{t("opSettings.default")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {vatRates.filter(v => v.is_active).sort((a, b) => b.rate - a.rate).map(v => (
                  <tr key={v.id}>
                    <td>{v.code}</td>
                    <td>{v.name}</td>
                    <td style={{ textAlign: "right" }}>{v.rate}%</td>
                    <td style={{ textAlign: "center" }}>
                      <input
                        type="radio"
                        name="default_vat"
                        checked={v.is_default}
                        onChange={() => updateVatRate(v.id, { is_default: true })}
                      />
                    </td>
                    <td>
                      <button className="ghost" style={{ color: "var(--red)" }} onClick={() => deleteVatRate(v.id)}>
                        <IconTrash size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <form onSubmit={createVatRate} style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <div>
                <label>{t("customers.colCode")}</label>
                <input value={newRate.code} onChange={e => setNewRate({ ...newRate, code: e.target.value.toUpperCase() })} maxLength={1} placeholder={t("opSettings.codePh")} style={{ width: 50 }} />
              </div>
              <div>
                <label>{t("common.name")}</label>
                <input value={newRate.name} onChange={e => setNewRate({ ...newRate, name: e.target.value })} placeholder={t("opSettings.namePh")} style={{ width: 180 }} />
              </div>
              <div>
                <label>{t("opSettings.ratePct")}</label>
                <input type="number" step="0.01" value={newRate.rate} onChange={e => setNewRate({ ...newRate, rate: e.target.value })} placeholder="10" style={{ width: 90 }} />
              </div>
              <button type="submit" className="primary"><IconPlus size={16} /> {t("opSettings.addRate")}</button>
            </form>
          </>
        ) : (
          <p style={{ color: "var(--muted)", fontSize: 14 }}>{t("opSettings.notVatPayerHint")}</p>
        )}
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>{t("opSettings.stockLevels")}</h3>
        <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 16 }}>
          {t("opSettings.stockLevelsHint")}
        </p>
        <Toggle
          checked={profile.stock_include_open_orders}
          onChange={(checked) => saveStockIncludeOpen(checked)}
          label={t("opSettings.stockIncludeOpen")}
          disabled={saving}
        />
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>{t("opSettings.customPayments")}</h3>
        <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 16 }}>
          {t("opSettings.customPaymentsHint")}
        </p>
        {payMethods.length > 0 && (
          <table style={{ marginBottom: 16 }}>
            <thead>
              <tr>
                <th></th>
                <th>{t("common.name")}</th>
                <th>{t("customers.colCode")}</th>
                <th>{t("opSettings.pmColor")}</th>
                {profile.eet_mode !== "disabled" && <th>{t("opSettings.pmEet")}</th>}
                <th>{t("catalog.colSort")}</th>
                <th>{t("common.status")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {[...payMethods].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)).map((m) => (
                <tr key={m.id} style={{ opacity: m.is_active ? 1 : 0.5 }}>
                  <td style={{ fontSize: 20 }}>{m.icon}</td>
                  <td style={{ fontWeight: 600 }}>{m.name}</td>
                  <td className="mono">{m.code}</td>
                  <td>
                    <span style={{ display: "inline-block", width: 14, height: 14, borderRadius: 4, background: m.color || "var(--muted)", verticalAlign: "middle" }} />
                  </td>
                  {profile.eet_mode !== "disabled" && (
                    <td>
                      <Toggle
                        checked={m.eet_enabled}
                        onChange={(checked) => togglePayMethod(m, { eet_enabled: checked })}
                      />
                    </td>
                  )}
                  <td>{m.sort_order}</td>
                  <td>
                    <Toggle
                      checked={m.is_active}
                      onChange={(checked) => togglePayMethod(m, { is_active: checked })}
                    />
                  </td>
                  <td>
                    <button className="ghost" onClick={() => {
                      setEditingPm(m);
                      setPmForm({ code: m.code, name: m.name, icon: m.icon, color: m.color || "#4f7cff", eet_enabled: m.eet_enabled, sort_order: m.sort_order });
                    }}>
                      {t("common.edit")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <form onSubmit={savePayMethod} style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <label>{t("customers.colCode")}</label>
            <input
              value={pmForm.code}
              onChange={(e) => setPmForm({ ...pmForm, code: e.target.value.toLowerCase() })}
              placeholder={t("opSettings.pmCodePh")}
              style={{ width: 120 }}
              disabled={!!editingPm}
              title={editingPm ? t("opSettings.pmCodeLocked") : undefined}
            />
          </div>
          <div>
            <label>{t("common.name")}</label>
            <input value={pmForm.name} onChange={(e) => setPmForm({ ...pmForm, name: e.target.value })} placeholder={t("opSettings.pmNamePh")} style={{ width: 160 }} />
          </div>
          <div>
            <label>{t("opSettings.pmIcon")}</label>
            <select value={pmForm.icon} onChange={(e) => setPmForm({ ...pmForm, icon: e.target.value })} style={{ width: 60 }}>
              {PM_ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div>
            <label>{t("opSettings.pmColor")}</label>
            <input type="color" value={pmForm.color} onChange={(e) => setPmForm({ ...pmForm, color: e.target.value })} style={{ width: 44, height: 34, padding: 2 }} />
          </div>
          <div>
            <label>{t("catalog.colSort")}</label>
            <input type="number" value={pmForm.sort_order} onChange={(e) => setPmForm({ ...pmForm, sort_order: parseInt(e.target.value) || 0 })} style={{ width: 70 }} />
          </div>
          {profile.eet_mode !== "disabled" && (
            <div>
              <label>{t("opSettings.pmEet")}</label>
              <Toggle
                checked={pmForm.eet_enabled}
                onChange={(checked) => setPmForm({ ...pmForm, eet_enabled: checked })}
              />
            </div>
          )}
          <button type="submit" className="primary" disabled={saving}>
            <IconPlus size={16} /> {editingPm ? t("common.save") : t("opSettings.pmAdd")}
          </button>
          {editingPm && (
            <button type="button" className="ghost" onClick={() => { setEditingPm(null); setPmForm(emptyPm); }}>
              {t("common.cancel")}
            </button>
          )}
        </form>
      </div>

      <NumberingSettings />
    </div>
  );
}
